import { MAX_CAPACITY_KWP, MAX_PV_SYSTEMS } from '../../constants';
import { PvInstallationInput } from '../../graphql/generated';

export { MAX_PV_SYSTEMS };

export type PVSystem = {
  year: string;
  month: string;
  capacity: string;
  installedBeforeFeb25?: boolean;
};

export type PVSystemData = {
  pvSystems: PVSystem[];
  feedIn: string;
  pvSystemsShareMarketLocation: boolean;
};

export const EMPTY_PV_SYSTEM: PVSystem = {
  year: '',
  month: '',
  capacity: '',
};

const currentYear = new Date().getFullYear();

export const YEAR_OPTIONS = Array.from({ length: currentYear - 1990 + 1 }, (_, i) => {
  const year = currentYear - i;
  return { value: year.toString(), label: year.toString() };
});

const parseCommissioningDate = (pv: PVSystem): string => {
  const isFeb2025 = pv.year === '2025' && pv.month === '2';
  const day = isFeb2025 ? (pv.installedBeforeFeb25 ? '01' : '26') : '01';
  return `${pv.year}-${pv.month.padStart(2, '0')}-${day}`;
};

const isCompletePvSystem = (pv: PVSystem): boolean => Boolean(pv.capacity && pv.year && pv.month);

const parseFeedIn = (feedIn: string): number | undefined => {
  if (!feedIn) return undefined;
  const parsed = Number.parseInt(feedIn, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Build the pvInstallation input from PV form data.
 * Returns undefined if required PV fields are incomplete.
 * When `requireFeedIn` is true, feedIn must be present for the input to be built.
 */
export const buildPvInstallationInput = (
  pvData?: PVSystemData,
  requireFeedIn = true,
): PvInstallationInput | undefined => {
  if (!pvData || !isCompletePvSystem(pvData.pvSystems[0])) return undefined;

  const feedInValue = parseFeedIn(pvData.feedIn);
  const hasValidFeedIn = feedInValue !== undefined;

  if (requireFeedIn && !hasValidFeedIn) return undefined;

  const subSystems: NonNullable<PvInstallationInput['photovoltaicSystem']>['subSystems'] = [];

  pvData.pvSystems.forEach((pv) => {
    if (!isCompletePvSystem(pv)) return;

    const capacity = Number.parseFloat(pv.capacity);
    if (Number.isNaN(capacity) || capacity >= MAX_CAPACITY_KWP) return;

    subSystems.push({
      peakPower: Math.round(capacity),
      commissioningDate: parseCommissioningDate(pv),
    });
  });

  if (subSystems.length === 0) return undefined;

  return {
    expectedFeedInPerYear: feedInValue ?? 0,
    photovoltaicSystem: {
      subSystems,
    },
  };
};
